import { useCallback, useEffect, useRef, useState } from "react";
import { AI_BASE_URL, aiHeaders } from "../api/aiClient";
import { SchoolReport } from "../types";

export interface SchoolMatch {
  school_id: number;
  school_name: string;
  region: string;
}

/**
 * School search and report loading for the standalone report screen.
 *
 * Search is debounced and every response is checked against the query that is
 * current when it lands — a slow reply for "kv m" must not overwrite results
 * for "kv mathura", which is what makes a search box feel haunted.
 */
export function useSchoolReport() {
  const [term, setTerm]       = useState("");
  const [matches, setMatches] = useState<SchoolMatch[]>([]);
  const [searching, setSearching] = useState(false);

  const [report, setReport]   = useState<SchoolReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  // The term this component is currently interested in. Compared on arrival so
  // out-of-order responses are dropped rather than rendered.
  const wantedRef = useRef("");

  useEffect(() => {
    const query = term.trim();
    wantedRef.current = query;

    if (query.length < 3) {
      setMatches([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const response = await fetch(
          `${AI_BASE_URL}/api/v1/analytics/schools/search?name=${encodeURIComponent(query)}`,
          { headers: aiHeaders() },
        );
        if (wantedRef.current !== query) return;      // a newer search overtook this one
        if (!response.ok) { setMatches([]); return; }
        const payload = await response.json();
        setMatches((payload.matches ?? []) as SchoolMatch[]);
      } catch {
        if (wantedRef.current === query) setMatches([]);
      } finally {
        if (wantedRef.current === query) setSearching(false);
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [term]);

  const openSchool = useCallback(async (schoolId: number) => {
    setLoading(true);
    setError(null);
    setReport(null);
    try {
      const response = await fetch(
        `${AI_BASE_URL}/api/v1/analytics/schools/${schoolId}/report`,
        { headers: aiHeaders() },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        setError(
          typeof body?.detail === "string"
            ? body.detail
            : response.status === 403
              ? "This school is outside your access scope."
              : "The report could not be loaded.",
        );
        return;
      }
      setReport((await response.json()) as SchoolReport);
      setMatches([]);
    } catch (err: unknown) {
      setError((err as Error).message || "The report could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  return { term, setTerm, matches, searching, report, loading, error, openSchool };
}
